import { Drama } from "@/types/video";
import { getDramaCoverUrl } from "@/services/firebase/drama";
import { useDisplayAdjustments } from "@/hooks/useDisplayAdjustments";
import { dramaAdjustment } from "@/services/firebase/displayMetric";
import { formatTaipeiHour } from "@/utils/publishSchedule";

interface DramaCardProps {
  drama: Drama;
  onClick: () => void;
  /** 該劇本月廣告解鎖次數（undefined = 還在 loading 或未開啟廣告解鎖） */
  monthlyAdUnlockCount?: number;
}

export default function DramaCard({ drama, onClick, monthlyAdUnlockCount }: DramaCardProps) {
  // 同步 build 公開 CDN URL,瀏覽器自動 disk-cache,無需 useEffect
  const coverUrl = getDramaCoverUrl(drama.drama_cover_url);
  const title = drama.drama_title || "未命名劇集";
  const isReleased = drama.isReleased || false;
  const newRelease = drama.new_release || false;
  const adUnlockEnabled = drama.ad_unlock_enabled === true;
  const dramaLength = drama.drama_length || 0;
  const { data: displayAdjustments } = useDisplayAdjustments();
  const playTimes =
    (drama.drama_play_times || 0) - dramaAdjustment(displayAdjustments, drama.id, "drama_play_times");

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`查看劇集 ${title} 的詳細資訊`}
      className="group cursor-pointer bg-white dark:bg-surface-800 rounded-xl shadow-sm 
                 hover:shadow-lg transition-all duration-300 overflow-hidden
                 border border-surface-200 dark:border-surface-700
                 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
    >
      {/* Cover Image */}
      <div
        className={`relative overflow-hidden bg-surface-200 dark:bg-surface-700 ${
          // 封面一律 2:3 直式主視覺（橫式劇也是），框不再依方向切 16:9
          "aspect-[2/3]"
        }`}
      >
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={`${title} 封面`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='600'%3E%3Crect fill='%23e5e7eb' width='400' height='600'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239ca3af' font-family='sans-serif' font-size='24'%3E無封面%3C/text%3E%3C/svg%3E";
              target.onerror = null; // 防止無限循環
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-surface-400">
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}

        {/* Status Badges */}
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          {drama.drama_video_orientation === 1 && (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-600 text-white shadow-sm">
              橫式
            </span>
          )}
          {isReleased && (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-success text-success-foreground shadow-sm">
              已上架
            </span>
          )}
          {!isReleased && drama.scheduled_publish_at && (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-violet-600 text-white shadow-sm">
              預約 {formatTaipeiHour(drama.scheduled_publish_at)}
            </span>
          )}
          {newRelease && (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-info text-info-foreground shadow-sm">
              新上架
            </span>
          )}
          {adUnlockEnabled && (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-500 text-white shadow-sm">
              廣告解鎖
            </span>
          )}
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4">
        <h3 className="font-semibold text-surface-900 dark:text-white mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {title}
        </h3>
        {drama.drama_title_en && (
          <p className="text-xs text-surface-500 dark:text-surface-400 mb-2 line-clamp-1">
            {drama.drama_title_en}
          </p>
        )}
        <div className="flex flex-col gap-1 text-sm text-surface-600 dark:text-surface-400">
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <span>{dramaLength} 集</span>
          </div>
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span>{playTimes.toLocaleString()} 次觀看</span>
          </div>
          {adUnlockEnabled && (
            <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>本月廣告解鎖 {(monthlyAdUnlockCount ?? 0).toLocaleString()} 次</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



